import {Attributes} from 'platform/components';
import {HStack, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {Nullish} from 'utility-types';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, parseDate, suffixTestId} from 'shared/web';

const AI_FLUID_PART_SHAPE = {
  nextDate: null as string | null,
  nextMileage: null as number | null,
};

type AiFluidPart = typeof AI_FLUID_PART_SHAPE;

interface VehicleServiceIntervalFluidAttributesProps extends RequiredTestIdProps {
  part:
    | {
        lastReplacementDate: string | Nullish;
        lastReplacementMileage: number | Nullish;
        nextReplacementMileage: number | Nullish;
        nextReplacementDate: string | Nullish;
      }
    | Nullish;
  aiPart?: AiFluidPart | Nullish;
}

export function VehicleServiceIntervalFluidAttributes(
  props: VehicleServiceIntervalFluidAttributesProps
) {
  const formatDateTime = useDateTimeFormatter();
  const hasAiPart = Boolean(props.aiPart);

  return (
    <Attributes
      rows={[
        {
          label: i18n.t('entity.vehicle.labels.dateOfLastReplacement'),
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.lastReplacementDate
                        ? formatDateTime('dateShort', parseDate(props.part.lastReplacementDate))
                        : EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                ),
              }
            : {
                value: props.part?.lastReplacementDate
                  ? formatDateTime('dateShort', parseDate(props.part.lastReplacementDate))
                  : null,
              }),
        },
        {
          label: `${i18n.t('entity.vehicle.labels.mileageAtLastReplacement')} (${i18n.t('general.metric.km')})`,
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.lastReplacementMileage ?? EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                ),
              }
            : {value: props.part?.lastReplacementMileage}),
        },
        {
          label: i18n.t('entity.vehicle.labels.replacementAfterMonths'),
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.nextReplacementMileage ?? EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">{props.aiPart?.nextMileage ?? EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                ),
              }
            : {value: props.part?.nextReplacementMileage}),
        },
        {
          label: i18n.t('entity.vehicle.labels.nextReplacementDate'),
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.nextReplacementDate
                        ? formatDateTime('dateShort', parseDate(props.part.nextReplacementDate))
                        : EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">
                      {props.aiPart?.nextDate
                        ? formatDateTime('dateShort', parseDate(props.aiPart.nextDate))
                        : EMPTY_PLACEHOLDER}
                    </Text>
                  </HStack>
                ),
              }
            : {
                value: props.part?.nextReplacementDate
                  ? formatDateTime('dateShort', parseDate(props.part.nextReplacementDate))
                  : null,
              }),
        },
      ]}
      data-testid={suffixTestId('part', props)}
    />
  );
}
